import type { CSSProperties } from 'react'
import { useState } from 'react'
import { paidBarPct, paymentStateToken, useVoidBookingPayment } from '@/entities/booking'
import type { BookingPayments } from '@/entities/booking'
import { problemText } from '@/shared/api'
import { SectionCard } from '@/shared/ui'
import { BookingPaymentDialog } from './BookingPaymentDialog'
import s from './BookingPaymentsCard.module.css'

/**
 * Секция 4 — «Платежи» (`prototype/Prototype.dc.html:2354`).
 *
 * Позиции сверху, итог с прогресс-баром снизу. Всё видимое приходит с сервера готовым: суммы
 * строками `amountLabel`, подписи состояний — `stateLabel` (инвариант контракта №3: деньги едут
 * копейками, человеческую строку собирает сервер). Фронт выбирает только ЦВЕТ (палитра
 * живёт на фронте, иначе смена темы требовала бы деплоя бекенда), а незнакомое состояние остаётся на
 * месте, но серым — ни «оплачено», ни «ожидается» (разбор — в `entities/booking/model/payment.ts`).
 *
 * ⚠️ ПРОЦЕНТЫ ФРОНТ НЕ СЧИТАЕТ: `paidPct` приходит оттуда же, где считаются `totalLabel` и
 * `paidLabel`. Пересчитать его из строк («37 200 ₽» → 37200) значило бы парсить человеческий текст,
 * который сервер вправе поменять («37,2К»). Единственное, что фронт делает с числом, — обрезает его
 * до [0, 100] (`paidBarPct`).
 */
export function BookingPaymentsCard({
  bookingId,
  bookingCode,
  payments,
}: {
  bookingId: string
  /** Код брони — чтобы отказ, долетевший после ухода с неё, называл, о какой брони речь. */
  bookingCode: string
  payments: BookingPayments
}) {
  const pct = paidBarPct(payments.paidPct)
  const voidPayment = useVoidBookingPayment(bookingId, `Отмена записи платежа по брони ${bookingCode}`)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)

  return (
    <SectionCard
      className={s.card}
      eyebrow="Платежи"
      /*
       * Процент в шапке — ОБРЕЗАННЫЙ, тот же самый, что рисует полосу и что слышит скринридер.
       *
       * ⚠️ Сырой `payments.paidPct` здесь не годится: при `paidPct = -5` глаз прочтёт «−5 %»,
       * полоса будет пуста, а `aria-valuenow` скажет «0» — три ответа на один вопрос.
       */
      aside={<span className={s.pct}>{pct}%</span>}
    >
      <div className={s.body}>
        {payments.items.map((item) => (
          /*
           * Ключ — `id` записи. Раньше своего идентификатора у позиции не было и ключом служил
           * `title`; с появлением отмены этого мало: две записи «Наличные» за один день различает
           * только он, и React перепутал бы, какую из них гасить.
           */
          <div key={item.id} className={s.item} data-voided={item.state === 'voided' || undefined}>
            <div className={s.itemBody}>
              <div className={s.title}>{item.title}</div>
              {/* `note` НЕобязателен (`note?: string` у генератора), и пустая строка тоже законна:
                  «чем платили» сервер знает не всегда. Пустая моно-строка под заголовком дала бы
                  лишние 2px и ничего больше. */}
              {item.note && <div className={s.note}>{item.note}</div>}
              {/*
               * ⚠️ ОТКУДА ЗАПИСЬ — ЕДИНСТВЕННОЕ, ЧЕМ СТРОКА ПЛОЩАДКИ ОТЛИЧАЕТСЯ ОТ СВОЕЙ НА ГЛАЗ.
               * Поле объявлено контрактом ровно ради этого, приходило готовой фразой и не выводилось
               * НИКУДА: менеджеру оставалось догадываться по наличию кнопки «Отменить» — то есть
               * читать происхождение по кнопке, которой у половины строк нет по другой причине.
               */}
              {item.originLabel && <div className={s.origin}>{item.originLabel}</div>}
            </div>

            <div className={s.itemRight}>
              <span className={s.amount}>{item.amountLabel}</span>
              {/* Токен — инлайн-переменной, как у чипа статуса: один класс пилюли под любое
                  состояние, без ветвления в CSS. */}
              <span
                className={s.pill}
                style={{ '--tint': paymentStateToken(item.state) } as CSSProperties}
              >
                <span className={s.pillDot} aria-hidden="true" />
                {item.stateLabel}
              </span>

              {/*
               * ⚠️ КНОПКУ ОТМЕНЫ РАЗРЕШАЕТ СЕРВЕР, А НЕ ЭКРАН. `canVoid` приходит из контракта: у
               * домена признак ручной записи — непустой идентификатор записавшего, и выводить
               * кнопку из него значило бы поселить чужое правило у себя. Строку площадки сервер
               * отменить не даст (422) — источник истины по ней площадка, и наша правка была бы
               * стёрта следующим её снапшотом.
               *
               * Подтверждение — прямо в строке, а не окном: отменяют ОДНУ запись, и переносить
               * выбор в модалку значило бы оторвать вопрос от того, к чему он относится.
               */}
              {item.canVoid &&
                (confirming === item.id ? (
                  <span className={s.confirm}>
                    <button
                      type="button"
                      className={s.confirmYes}
                      disabled={voidPayment.isPending}
                      onClick={() => voidPayment.mutate(item.id)}
                    >
                      {voidPayment.isPending ? 'Отменяем…' : 'Точно отменить'}
                    </button>
                    <button
                      type="button"
                      className={s.confirmNo}
                      onClick={() => setConfirming(null)}
                    >
                      Нет
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={s.void}
                    onClick={() => setConfirming(item.id)}
                  >
                    Отменить
                  </button>
                ))}
            </div>
          </div>
        ))}

        <div className={s.total}>
          <div className={s.totalRow}>
            <span className={s.totalLabel}>Оплачено из {payments.totalLabel}</span>
            <span className={s.totalAmount}>{payments.paidLabel}</span>
          </div>

          {/*
           * ⚠️ ОЖИДАЕТСЯ — ОТДЕЛЬНАЯ ВЕЛИЧИНА, А НЕ СЛАГАЕМОЕ. «Получено 26 040» и «должен 26 040»
           * — противоположные утверждения; так это поле и заведено у домена.
           *
           * ⚠️ ЧЕСТНАЯ ОГОВОРКА: на простой броне два числа складываются в сумму строкой выше
           * (11 160 + 26 040 = 37 200) и читаются как разбивка. Совпадение настоящее, но НЕ ПРАВИЛО:
           * состоявшийся возврат уменьшает оплаченное, не трогая выставленного (у отменённой брони
           * «Оплачено 0 ₽» против суммы 24 800 ₽, а этой строки нет вовсе), а неоплаченный ЗАЛОГ
           * входит сюда и не входит в сумму брони. Поэтому строки стоят порознь, без знака равенства.
           */}
          {payments.pendingLabel && (
            <div className={s.asideRow}>
              <span className={s.asideLabel}>Ожидается</span>
              <span className={s.asideAmount}>{payments.pendingLabel}</span>
            </div>
          )}

          {/*
           * ⚠️ ЗАЛОГ — ОТДЕЛЬНОЙ СТРОКОЙ, А НЕ ВНУТРИ ОПЛАЧЕННОГО. Макет складывал его с
           * предоплатой (16 160 = 11 160 + 5 000); домен держит его своей величиной, потому что это
           * чужие деньги на хранении, а не выручка. Сложив, экран сказал бы, что гость заплатил на
           * пять тысяч больше, чем заплатил.
           *
           * Строки нет, когда залога не было: «Залог 0 ₽» утверждал бы, что его брали и вернули, а
           * это другое событие. То же у возврата и штрафов.
           */}
          {payments.depositLabel && (
            <div className={s.asideRow}>
              <span className={s.asideLabel}>Залог на хранении</span>
              <span className={s.asideAmount}>{payments.depositLabel}</span>
            </div>
          )}
          {payments.refundedLabel && (
            <div className={s.asideRow}>
              <span className={s.asideLabel}>Возвращено</span>
              <span className={s.asideAmount}>{payments.refundedLabel}</span>
            </div>
          )}
          {payments.finesLabel && (
            <div className={s.asideRow}>
              <span className={s.asideLabel}>Удержано штрафами</span>
              <span className={s.asideAmount}>{payments.finesLabel}</span>
            </div>
          )}

          {/*
           * Дорожка и заливка. `role="progressbar"` с тремя aria-атрибутами — потому что это и
           * есть прогресс-бар: без них скринридер увидит два пустых `div`, а процент стоит
           * в шапке секции, в двадцати сантиметрах отсюда, и связи между ними нет никакой.
           *
           * `aria-valuenow` — ОБРЕЗАННОЕ значение, то же самое, что нарисовано и что стоит в шапке:
           * озвучивать «-5%» там, где полоса пуста, значило бы развести глаз и ухо.
           *
           * ⚠️ ПОЧЕМУ НЕ `<progress>`, ХОТЯ ЛИНТ ПРАВ ПО СУЩЕСТВУ. Привычное возражение «он не
           * стилизуется под дорожку макета» ПРОВЕРЕНО ЗОНДОМ И ЛОЖНО: `appearance:none` плюс
           * `::-webkit-progress-bar/-value` дают ту же дорожку по всем замеренным свойствам: h 5px,
           * radius 3px, `--sunk` под `--accent`, заливка в тот же пиксель.
           *
           * Настоящая причина — ВЕНДОРНЫЕ ПСЕВДОЭЛЕМЕНТЫ: заливка живёт в теневом дереве, и добраться
           * до неё можно только через `::-webkit-progress-value` И `::-moz-progress-bar`, то есть
           * двумя правилами на два движка. Chromium проверен, а Firefox НЕДОСТУПЕН ПО ПОСТРОЕНИЮ:
           * харнесс зовёт `chromium.launch` и берёт установленный `chrome` либо `msedge`, оба
           * Chromium (`tools/shot.mjs`). `::-moz-progress-bar` уехал бы в прод непроверенным
           * утверждением о движке, которого я не видел; меняем, когда в харнессе появится второй.
           */}
          <div
            className={s.track}
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Оплачено из ${payments.totalLabel}`}
          >
            {/*
             * Единственная ВЫЧИСЛЯЕМАЯ ГЕОМЕТРИЯ секции — только ради неё здесь инлайн. Правило
             * проекта запрещает инлайном ВЁРСТКУ, а не переменные: `--tint` у пилюли выше — инъекция
             * токена, устоявшаяся идиома проекта, и под запрет она не подпадает.
             */}
            <div className={s.fill} style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/*
         * «Записать платёж» — ПОД сводом, а не в шапке секции: там уже стоит процент, и две вещи
         * в одном слоте разъехались бы на первой же длинной сумме. Кнопка внизу читается как
         * продолжение ленты, которую она пополняет.
         *
         * ⚠️ Отказ показывает окно, поэтому здесь ошибки отмены — и только они. Ошибку записи
         * рисовать тут было бы вторым ответом об одном событии.
         */}
        {voidPayment.isError && (
          <p className={s.error} role="alert">
            {problemText(voidPayment.error, 'Не удалось отменить запись')}
          </p>
        )}

        <button type="button" className={s.record} onClick={() => setRecording(true)}>
          Записать платёж
        </button>
      </div>

      {/*
       * Монтируется ПО ФАКТУ ОТКРЫТИЯ — иначе состояние формы и ключ идемпотентности пережили бы
       * закрытие, и второе открытие показало бы брошенный черновик с ключом от прошлой попытки.
       * Тот же разбор и та же ошибка, что были у окна правки брони.
       */}
      {recording && (
        <BookingPaymentDialog
          bookingId={bookingId}
          bookingCode={bookingCode}
          open
          onOpenChange={setRecording}
        />
      )}
    </SectionCard>
  )
}
